/**
 * v0.19.20 — 「表单原语」的**共用入口** (本仓铁律「一个逻辑一个入口, 不许抄两遍」)
 *
 * ## 为什么抽出来
 *   `renderSection` / `fieldIcon` / `errText` 这三个表单原语, 改前**各被抄了 4 份**
 *   (散在 8 个弹层 / tab 里) —— 一份改一次, 迟早分叉 (这正是本仓反复踩的坑)。
 *   ⇒ 收进这里的**模块级函数**, 各处 import。
 *
 * ## ⛔ 不许在这里改 DOM 结构
 *   `renderSection` 的骨架 (`.ZS-section` → `.ZS-section-head`(编号 / 标题 / caption)
 *   → `.ZS-section-body`) 是「新建客户跟进」上线时的既定排版, 多个弹层**逐字共用**。
 *   任何结构性改动都会同时改掉多个弹层的观感 ⇒ 想改必须先问辉哥。
 *
 * ## ⛔ JSX 空白 (实测过的坑, 照抄时别改)
 *   Babel 的 JSX transform 会**丢掉**「只含空白且带换行」的文本节点,
 *   但**同一行**两个元素之间的空格会**保留**成空白文本节点。
 *   ⇒ 下面 `.ZS-section-head` 里的三个 span **必须写在同一行且彼此无空格**
 *     (照抄 v0.19.18 `utils/FollowupForm.js` 那份) ⇒ 渲染出来的 DOM 逐字节不变。
 *
 * ## 配套样式
 *   `less/forum.less` 的 `.ZS-followup-form { .ZS-section / .ZS-section-head / .ZS-section-body }`
 *   —— 皮肤统一由那里负责 (⛔ 不给本文件里的 class 另写一套皮肤)。
 */

/**
 * 一段分区。
 *
 * @param {number|string} num      编号 (1 / 2 / 3 …) —— 渲染成标题栏左侧的方块
 * @param {string}        title    分区标题
 * @param {string|null}   caption  标题栏右侧的说明 (窄屏会被隐藏, 见 less 的 @media 段)
 * @param {*}             children 分区内容 (通常是若干 `.ZS-form-row`)
 * @param {object}        [opts]
 * @param {boolean}       [opts.conditionalCaption=false] caption 为空 / null 时**不渲染** `.c` 那个 span。
 *        改前只有「转学员」modal (`ConvertStudentModal.js`) 那一份是**条件渲染**
 *        (它的「体验卡处理」/「本次操作」两个分区 caption 传 `null`),
 *        其余三份都是**无条件渲染** ⇒ 用这个开关把两种语义都表达出来, 不许二选一。
 * @param {*}             [opts.extra] 标题行**末尾**的附加内容 (如「共 N 张」计数 chip)。
 *        ⚠️ v0.19.20 — 这**不是**死参数: 「转学员」modal 的「体验卡处理」分区**真在传**
 *        一个 `.ZSC-chip.ZSC-trial-count` 的「共 N 张」chip (窄屏 `.c` 是 `display:none`,
 *        那个 chip 故意不进 `.c`) ⇒ 拿掉它 = DOM 少一个可见元素 ⇒ 本文件**保留**这个插槽。
 *
 * @returns {JSX.Element}
 */
export function renderSection(num, title, caption, children, opts) {
  const o = opts || {};
  const captionSpan = o.conditionalCaption ? caption ? m("span", {
    className: "c"
  }, caption) : null : m("span", {
    className: "c"
  }, caption);
  return m("div", {
    className: "ZS-section"
  }, m("div", {
    className: "ZS-section-head"
  }, m("span", {
    className: "n"
  }, num), m("span", {
    className: "t"
  }, title), captionSpan, o.extra), m("div", {
    className: "ZS-section-body"
  }, children));
}

/**
 * 字段前的图标 (⛔ Font Awesome, 不用 emoji)。
 *
 * @param {string} icon FA 图标名 (如 'fa-user')
 */
export function fieldIcon(icon) {
  return m("i", {
    className: 'fas ' + icon,
    style: "color:var(--zs-text-2-8d96a3, #8d96a3);"
  });
}

/**
 * 后端错误的 `detail` 文案 (⛔ 不自己编文案)。
 *
 * 四级兜底: `response.errors[0].detail` → `response.json.error` → `e.message` → `fallback`。
 *
 * @param {*}      e        捕获到的异常
 * @param {string} fallback 兜底文案 (⛔ **必传** —— 不传就会出现 `undefined` 显示在界面上)
 */
export function errText(e, fallback) {
  const detail = e && e.response && e.response.errors && e.response.errors[0] && e.response.errors[0].detail;
  if (detail) return String(detail);
  if (e && e.response && e.response.json && e.response.json.error) return String(e.response.json.error);
  if (e && e.message) return String(e.message);
  return fallback;
}

/**
 * 后端错误文案 —— **三级兜底**: `response.errors[0].detail` → `e.message` → `fallback`。
 *
 * ⚠️ **与本文件已有的 `errText()` 的区别（本仓最容易踩的坑）**:
 *   · `errText()` 是**四级**（多一档 `response.json.error`），是批 41 已上线的口径;
 *   · 本函数是**三级**，**与本仓 19 处历史手抄版逐字等价** ⇒ 用它替换手抄版是**零行为变化**;
 *   · ⛔ **不要**用手抄版去调 `errText()`（多一档 ⇒ 不是零行为变化）。
 *
 * @param {*}      e        捕获到的异常
 * @param {string} fallback 兜底文案 (⛔ **必传** —— 不传就会显示 `undefined`)
 */
export function errReason(e, fallback) {
  return e && e.response && e.response.errors && e.response.errors[0] && e.response.errors[0].detail || e && e.message || fallback;
}

/**
 * 后端错误文案 —— `errReason` 的**变体**: 额外把 `'[object Object]'` 这个占位串**当没有消息**。
 *
 * ⚠️ 为什么需要它: mithril 请求层偶尔把整个响应体塞进 `e.message` ⇒ 拼进 toast 会变成
 *   「取消约课失败: [object Object]」⇒ 等于没提示（实测过）。
 *
 * @param {*}      e        捕获到的异常
 * @param {string} fallback 兜底文案 (⛔ **必传**)
 */
export function errReasonSafe(e, fallback) {
  return e && e.response && e.response.errors && e.response.errors[0] && e.response.errors[0].detail || (e && e.message && e.message !== '[object Object]' ? e.message : '') || fallback;
}

/**
 * 后端错误文案 —— `errReason` 的**变体**: 额外把 `'null'` / `'undefined'` 这两个占位串**当没有消息**。
 *
 * ⚠️ 为什么需要它: 网络层失败（断网 / 请求被拦 / 超时）时 mithril 给的 `e.message` 有时是
 *   字符串 `"null"` ⇒ 直接拼进 toast 会变成「标记失败: null」⇒ 等于没提示（实测：拦掉 PATCH 就是这个）。
 *
 * @param {*}      e        捕获到的异常
 * @param {string} fallback 兜底文案 (⛔ **必传**)
 */
export function errReasonPlain(e, fallback) {
  return e && e.response && e.response.errors && e.response.errors[0] && e.response.errors[0].detail || (e && e.message && e.message !== 'null' && e.message !== 'undefined' ? e.message : '') || fallback;
}
flarum.reg.add('ziiven-ziven-scheduler', 'forum/utils/FormPrimitives', { renderSection: renderSection,fieldIcon: fieldIcon,errText: errText,errReason: errReason,errReasonSafe: errReasonSafe,errReasonPlain: errReasonPlain, });